import type { BookingItem } from 'hooks/useBookingItems';
import type { Discount, Space, Venue } from 'types/domain';
import type { BookingItemFilters } from 'types/filters';
import moment from './dayjs';
// utils
import { isChecked } from './dynamicFilters';

export const filteredBookingItems = (
  bookingItems: BookingItem[],
  filters: BookingItemFilters
) => {
  return bookingItems.filter(bookingItem => {
    const filterResults: boolean[] = [];

    if (filters.startDate) {
      filterResults.push(
        moment(filters.startDate).isSameOrBefore(bookingItem.startTime, 'day')
      );
    }

    if (filters.endDate) {
      filterResults.push(
        moment(filters.endDate).isSameOrAfter(bookingItem.endTime, 'day')
      );
    }

    if (filters.statuses.filter(isChecked).length > 0) {
      filterResults.push(
        filters.statuses.filter(isChecked).some(status => {
          return status.id === bookingItem.state;
        })
      );
    }

    if (filters.spaces.filter(isChecked).length > 0) {
      filterResults.push(
        filters.spaces.filter(isChecked).some(space => {
          return space.id === bookingItem.space.id;
        })
      );
    }

    return !filterResults.includes(false);
  });
};

export const filteredKeyFacets = <F extends { isKey: boolean }[]>(
  facets: F
): F => facets.filter(facet => facet.isKey) as F;

// NB: both sorts below return a boolean, which Array.prototype.sort coerces to
// 1/0 — so they only ever say "swap" or "leave", never "a before b". The
// `Number(...)` makes that coercion explicit rather than fixing it; see the
// follow-ups note.
const byDescendingAmount = (a: Discount, b: Discount) =>
  Number(b.amount > a.amount);

// Generic over the discount type so callers holding a richer discount (e.g. the
// client discounts, which carry campaign dates) get the same type back rather
// than having it narrowed to the fields matched on here.
export const filteredVenueDiscounts = <D extends Discount>(
  discounts: D[],
  venue: Pick<Venue, 'id' | 'spaces'>
): D[] => {
  const roomTypeIds = Array.from(
    new Set(venue.spaces?.flatMap(space => space.categoryIds))
  );

  const roomIds = Array.from(new Set(venue.spaces?.flatMap(space => space.id)));

  return discounts
    .filter(discount => {
      if (discount.spaceIds.length === 0 && discount.roomTypeIds.length === 0) {
        return discount.venueIds.includes(venue.id);
      }

      return (
        ((discount.venueIds.length === 0 ||
          discount.venueIds.includes(venue.id)) &&
          roomTypeIds.some(
            roomTypeId =>
              roomTypeId !== undefined &&
              discount.roomTypeIds.includes(roomTypeId)
          )) ||
        roomIds.some(spaceId => discount.spaceIds.includes(spaceId))
      );
    })
    .sort(byDescendingAmount);
};

export const filteredSpaceDiscounts = <D extends Discount>(
  discounts: D[],
  venue: Pick<Venue, 'id'>,
  space: Pick<Space, 'id' | 'categoryIds'>
): D[] =>
  discounts
    .filter(
      ({ venueIds = [], spaceIds = [], roomTypeIds = [] }) =>
        (venueIds.includes(venue.id) &&
          spaceIds.length === 0 &&
          roomTypeIds.length === 0) ||
        spaceIds?.includes(space.id) ||
        (venueIds.includes(venue.id) &&
          !!space.categoryIds &&
          roomTypeIds.includes(space.categoryIds[0]))
    )
    .sort(byDescendingAmount);
